import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TelegramPrSettings } from '@/components/TelegramPrSettings';
import { UnityVersionsSettings } from '@/components/UnityVersionsSettings';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { getSettings, updateSettings, runBackupNow } from '@/api/settings';
import { getTimezone, setTimezone, listTimezones, getBrowserTimezone } from '@/lib/datetime';
import { Select } from '@/components/Select';
import { getApps } from '@/api/apps';
import { DEFAULT_BRAND_NAME } from '@/hooks/useBranding';

/** Mirrors DEFAULT_PROTECTED_TERMS in the worker (lib/listing-copy.ts): what applies until the setting is saved. */
type SignalGate = 'motion_frozen' | 'tz_mismatch' | 'battery_full' | 'device_burst' | 'bot_fleet';

// Выключатели гейтов /device/report: admin_settings antimod_gate_<key> = '0'.
const SIGNAL_GATES: { key: SignalGate; label: string; help: string }[] = [
  { key: 'motion_frozen', label: 'Замёрзший сенсор', help: 'Все кадры акселерометра одинаковые — ферма или эмулятор.' },
  { key: 'tz_mismatch', label: 'Часовой пояс ≠ страна IP', help: 'Пояс устройства (из регистрации пушей) не бывает в стране IP.' },
  { key: 'battery_full', label: 'Батарея 100% на зарядке', help: 'Так стоят фермы; самый шумный из гейтов.' },
  { key: 'device_burst', label: 'Пачка одинаковых телефонов', help: 'Третий и дальше телефон той же модели с тем же зарядом на зарядке за 2 минуты (флот HRY-LX1T / OnePlus8Pro на 85%). Пойманный остаётся в блоке.' },
  { key: 'bot_fleet', label: 'Флот сканеров по сборке ОС', help: 'Сборка QKR1.191246.002/2006701342 — только у флота (HRY-LX1T / OnePlus8Pro / OnePlus A0001), ни одной конверсии.' },
];

/** Проверка «человек или бот»: admin_settings session_*, зеркало DEFAULT_SESSION_SETTINGS в worker (lib/device-session.ts). */
type SessionField = 'bot_min' | 'bot_min_confidence' | 'human_max' | 'human_min_confidence' | 'sample_sure_bot' | 'sample_clean';
const SESSION_DEFAULTS: Record<SessionField, string> = {
  bot_min: '60', bot_min_confidence: '50', human_max: '20', human_min_confidence: '80',
  sample_sure_bot: '10', sample_clean: '0',
};
const SESSION_FIELDS = Object.keys(SESSION_DEFAULTS) as SessionField[];

/** Пауза force offer после выхода в стор: admin_settings offer_hold_*, зеркало OFFER_HOLD_DEFAULTS в worker (lib/offer-hold.ts). */
type OfferHoldField = 'release_hours' | 'ramp_hours' | 'meta_hours' | 'build_hours' | 'spike_min' | 'spike_mult' | 'spike_hours';
const OFFER_HOLD_DEFAULTS: Record<OfferHoldField, string> = {
  release_hours: '120', ramp_hours: '48', meta_hours: '24', build_hours: '24',
  spike_min: '20', spike_mult: '5', spike_hours: '6',
};
const OFFER_HOLD_FIELDS = Object.keys(OFFER_HOLD_DEFAULTS) as OfferHoldField[];

/** Mirrors DEFAULT_TLDS / DEFAULT_MAX_PRICE in the worker (lib/app-domains.ts). */
const DEFAULT_APP_DOMAIN_TLDS = ['com', 'net', 'org', 'xyz', 'online', 'site', 'store', 'shop', 'app', 'dev', 'info', 'pro', 'live', 'fun', 'space', 'website', 'top', 'click', 'cc', 'me'];
const DEFAULT_APP_DOMAIN_MAX_PRICE = '15';
const DEFAULT_PROTECTED_TERMS = ['Fortune Tiger', 'Fortune Ox', 'Fortune Rabbit', 'Chicken Road', 'Aviator', 'Plinko', 'Blackjack', 'Baccarat', 'Sweet Bonanza', 'Gates of Olympus'];

function ThresholdField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs text-content-subtle mb-1">
        {label}
      </label>
      <input
        id={id}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
      />
    </div>
  );
}

export function Settings() {
  const queryClient = useQueryClient();
  const [brandName, setBrandName] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [geminiKey, setGeminiKey] = useState('');
  const [googleNlKey, setGoogleNlKey] = useState('');
  const [protectedTerms, setProtectedTerms] = useState('');
  const [reveKey, setReveKey] = useState('');
  const [factoryPat, setFactoryPat] = useState('');
  const [factoryApiHost, setFactoryApiHost] = useState('');
  const [renderProvider, setRenderProvider] = useState<'reve' | 'gpt'>('reve');
  const [taskConcurrency, setTaskConcurrency] = useState('6');
  const [pwaMirrorBase, setPwaMirrorBase] = useState('');
  const [emailApiBase, setEmailApiBase] = useState('');
  const [emailApiToken, setEmailApiToken] = useState('');
  const [googleAdsBase, setGoogleAdsBase] = useState('');
  const [googleAdsToken, setGoogleAdsToken] = useState('');
  const [ga4Enabled, setGa4Enabled] = useState(true);
  const [ga4EventReg, setGa4EventReg] = useState('');
  const [ga4EventDep, setGa4EventDep] = useState('');
  const [backupChatId, setBackupChatId] = useState('');
  const [templateAppId, setTemplateAppId] = useState('');
  const [motionStddevMin, setMotionStddevMin] = useState('8');
  const [motionDupMax, setMotionDupMax] = useState('90');
  const [motionGMin, setMotionGMin] = useState('8500');
  const [motionGMax, setMotionGMax] = useState('11000');
  const [signalGates, setSignalGates] = useState<Record<SignalGate, boolean>>(
    { motion_frozen: true, tz_mismatch: true, battery_full: true, device_burst: true, bot_fleet: true },
  );
  // Проверка «человек или бот» (lib/device-session.ts): только '1' включает.
  const [humanPass, setHumanPass] = useState(false);
  const [behaviorGate, setBehaviorGate] = useState(false);
  const [sessionCfg, setSessionCfg] = useState<Record<SessionField, string>>(SESSION_DEFAULTS);
  const [offerHold, setOfferHold] = useState<Record<OfferHoldField, string>>(OFFER_HOLD_DEFAULTS);
  const [offerHoldOn, setOfferHoldOn] = useState(true);
  const [offerSpikeOn, setOfferSpikeOn] = useState(true);
  // Store listing watch (lib/store-listing-watch.ts): '0' turns each off, anything else is on.
  const [listingWatch, setListingWatch] = useState(true);
  const [listingWatchAlerts, setListingWatchAlerts] = useState(true);
  // App domains (lib/app-domains.ts): '0' turns each switch off.
  const [appDomains, setAppDomains] = useState(true);
  const [appDomainAutoRenew, setAppDomainAutoRenew] = useState(true);
  const [appDomainTlds, setAppDomainTlds] = useState(DEFAULT_APP_DOMAIN_TLDS.join(', '));
  const [appDomainMaxPrice, setAppDomainMaxPrice] = useState(DEFAULT_APP_DOMAIN_MAX_PRICE);
  const [showOpenai, setShowOpenai] = useState(false);
  const [showGemini, setShowGemini] = useState(false);
  const [showGoogleNl, setShowGoogleNl] = useState(false);
  const [showReve, setShowReve] = useState(false);
  const [showFactoryPat, setShowFactoryPat] = useState(false);
  const [showEmailToken, setShowEmailToken] = useState(false);
  const [showGoogleAdsToken, setShowGoogleAdsToken] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tz, setTz] = useState<string>(getTimezone());
  const [tzSaved, setTzSaved] = useState(false);
  const browserTz = getBrowserTimezone();
  const zones = listTimezones();


  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  });

  const apps = useQuery({ queryKey: ['apps'], queryFn: getApps });

  useEffect(() => {
    if (settings) {
      setBrandName(settings.brand_name || '');
      setOpenaiKey(settings.openai_api_key || '');
      setGeminiKey(settings.gemini_api_key || '');
      setGoogleNlKey(settings.google_nl_api_key || '');
      // Never saved yet: the server uses its built-in list, shown here so it can be edited.
      setProtectedTerms(settings.listing_protected_terms ?? DEFAULT_PROTECTED_TERMS.join('\n'));
      setReveKey(settings.reve_api_key || '');
      setFactoryPat(settings.factory_github_pat || '');
      setFactoryApiHost(settings.factory_api_host || '');
      setRenderProvider(settings.screenshot_render_provider === 'gpt' ? 'gpt' : 'reve');
      setTaskConcurrency(settings.ai_task_concurrency || '6');
      setPwaMirrorBase(settings.pwa_mirror_base || '');
      setEmailApiBase(settings.email_api_base_url || '');
      setGoogleAdsBase(settings.google_ads_api_base_url || '');
      setGa4Enabled(settings.ga4_conversions_enabled !== '0');
      setGa4EventReg(settings.ga4_event_reg || '');
      setGa4EventDep(settings.ga4_event_dep || '');
      setBackupChatId(settings.backup_tg_chat_id || '');
      setTemplateAppId(settings.setup_template_app_id || '');
      setMotionStddevMin(settings.motion_stddev_min || '8');
      setMotionDupMax(settings.motion_dup_max || '90');
      setMotionGMin(settings.motion_g_min || '8500');
      setMotionGMax(settings.motion_g_max || '11000');
      setSignalGates({
        motion_frozen: settings.antimod_gate_motion_frozen !== '0',
        tz_mismatch: settings.antimod_gate_tz_mismatch !== '0',
        battery_full: settings.antimod_gate_battery_full !== '0',
        device_burst: settings.antimod_gate_device_burst !== '0',
        bot_fleet: settings.antimod_gate_bot_fleet !== '0',
      });
      setHumanPass(settings.antimod_human_pass === '1');
      setBehaviorGate(settings.antimod_gate_behavior_bot === '1');
      setSessionCfg(Object.fromEntries(SESSION_FIELDS.map((f) => [f, settings[`session_${f}`] || SESSION_DEFAULTS[f]])) as Record<SessionField, string>);
      setOfferHoldOn(settings.offer_hold_enabled !== '0');
      setOfferSpikeOn(settings.offer_hold_spike !== '0');
      setOfferHold(Object.fromEntries(OFFER_HOLD_FIELDS.map((f) => [f, settings[`offer_hold_${f}`] || OFFER_HOLD_DEFAULTS[f]])) as Record<OfferHoldField, string>);
      setListingWatch(settings.store_listing_watch !== '0');
      setListingWatchAlerts(settings.store_listing_watch_alerts !== '0');
      setAppDomains(settings.app_domains_enabled !== '0');
      setAppDomainAutoRenew(settings.app_domain_auto_renew !== '0');
      setAppDomainTlds(settings.app_domain_tlds || DEFAULT_APP_DOMAIN_TLDS.join(', '));
      setAppDomainMaxPrice(settings.app_domain_max_price || DEFAULT_APP_DOMAIN_MAX_PRICE);
    }
  }, [settings]);

  const [backupStarted, setBackupStarted] = useState(false);
  const backupMutation = useMutation({
    mutationFn: runBackupNow,
    onSuccess: () => {
      setBackupStarted(true);
      setTimeout(() => setBackupStarted(false), 8000);
    },
  });

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, string>) => updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const handleSave = () => {
    const data: Record<string, string> = {};
    data.brand_name = brandName.trim();
    if (openaiKey.trim()) data.openai_api_key = openaiKey.trim();
    if (geminiKey.trim()) data.gemini_api_key = geminiKey.trim();
    if (googleNlKey.trim()) data.google_nl_api_key = googleNlKey.trim();
    data.listing_protected_terms = protectedTerms.split(/[\n,]+/).map((t) => t.trim()).filter(Boolean).join('\n');
    if (reveKey.trim()) data.reve_api_key = reveKey.trim();
    if (factoryPat.trim()) data.factory_github_pat = factoryPat.trim();
    data.factory_api_host = factoryApiHost.trim().replace(/^[a-z]+:\/\//i, '').replace(/\/.*$/, '').toLowerCase();
    data.screenshot_render_provider = renderProvider;
    data.ai_task_concurrency = taskConcurrency;
    data.pwa_mirror_base = pwaMirrorBase.trim().replace(/\/+$/, '');
    data.email_api_base_url = emailApiBase.trim();
    if (emailApiToken.trim()) data.email_api_token = emailApiToken.trim();
    data.google_ads_api_base_url = googleAdsBase.trim().replace(/\/+$/, '');
    if (googleAdsToken.trim()) data.google_ads_api_token = googleAdsToken.trim();
    data.ga4_conversions_enabled = ga4Enabled ? '1' : '0';
    data.ga4_event_reg = ga4EventReg.trim();
    data.ga4_event_dep = ga4EventDep.trim();
    data.backup_tg_chat_id = backupChatId.trim();
    data.setup_template_app_id = templateAppId;
    data.motion_stddev_min = motionStddevMin.trim();
    data.motion_dup_max = motionDupMax.trim();
    data.motion_g_min = motionGMin.trim();
    data.motion_g_max = motionGMax.trim();
    for (const gate of SIGNAL_GATES) data[`antimod_gate_${gate.key}`] = signalGates[gate.key] ? '1' : '0';
    data.antimod_human_pass = humanPass ? '1' : '0';
    data.antimod_gate_behavior_bot = behaviorGate ? '1' : '0';
    for (const f of SESSION_FIELDS) data[`session_${f}`] = sessionCfg[f].trim() || SESSION_DEFAULTS[f];
    data.offer_hold_enabled = offerHoldOn ? '1' : '0';
    data.offer_hold_spike = offerSpikeOn ? '1' : '0';
    for (const f of OFFER_HOLD_FIELDS) data[`offer_hold_${f}`] = offerHold[f].trim() || OFFER_HOLD_DEFAULTS[f];
    data.store_listing_watch = listingWatch ? '1' : '0';
    data.store_listing_watch_alerts = listingWatchAlerts ? '1' : '0';
    data.app_domains_enabled = appDomains ? '1' : '0';
    data.app_domain_auto_renew = appDomainAutoRenew ? '1' : '0';
    data.app_domain_tlds = appDomainTlds.split(/[\s,]+/).map((t) => t.trim().replace(/^\./, '')).filter(Boolean).join(', ');
    data.app_domain_max_price = appDomainMaxPrice.trim();
    saveMutation.mutate(data);
  };

  const maskKey = (key: string) => {
    if (!key) return '';
    if (key.length <= 8) return '****';
    return key.slice(0, 4) + '****' + key.slice(-4);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-content">Settings</h1>
        <p className="text-sm text-content-subtle mt-1">
          Manage API keys and configuration
        </p>
      </div>

      {/* API Keys Section */}
      <div className="bg-surface border border-border rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium text-content">API Keys</h2>
          <p className="text-sm text-content-subtle mt-1">
            API keys used by the Generate section for AI-powered ad creation. If not set, environment variable defaults are used.
          </p>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* OpenAI API Key */}
          <div>
            <label htmlFor="openai-key" className="block text-sm font-medium text-content-muted mb-1">
              OpenAI API Key
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Used for text generation (GPT-4o-mini) — titles, descriptions, CTAs
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  id="openai-key"
                  type={showOpenai ? 'text' : 'password'}
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                  placeholder="sk-..."
                  className="block w-full px-3 py-2 pr-10 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowOpenai(!showOpenai)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-content-subtle hover:text-content-muted"
                >
                  {showOpenai ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {settings?.openai_api_key && !showOpenai && (
              <p className="text-xs text-success mt-1">
                Set: {maskKey(settings.openai_api_key)}
              </p>
            )}
          </div>

          {/* Gemini API Key */}
          <div>
            <label htmlFor="gemini-key" className="block text-sm font-medium text-content-muted mb-1">
              Gemini API Key
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Used for icon generation (Gemini) — app-style icons for custom banner ads
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  id="gemini-key"
                  type={showGemini ? 'text' : 'password'}
                  value={geminiKey}
                  onChange={(e) => setGeminiKey(e.target.value)}
                  placeholder="AIza..."
                  className="block w-full px-3 py-2 pr-10 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowGemini(!showGemini)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-content-subtle hover:text-content-muted"
                >
                  {showGemini ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {settings?.gemini_api_key && !showGemini && (
              <p className="text-xs text-success mt-1">
                Set: {maskKey(settings.gemini_api_key)}
              </p>
            )}
          </div>

          {/* Google Natural Language API Key */}
          <div>
            <label htmlFor="google-nl-key" className="block text-sm font-medium text-content-muted mb-1">
              Google Natural Language API Key
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Used by the Apps Setup wizard to classify the full description (Poker &amp; Casino Games / Gambling / Sports Betting) and rewrite it until the target categories score 0.5+. Leave empty to skip the check.
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  id="google-nl-key"
                  type={showGoogleNl ? 'text' : 'password'}
                  value={googleNlKey}
                  onChange={(e) => setGoogleNlKey(e.target.value)}
                  placeholder="AIza..."
                  className="block w-full px-3 py-2 pr-10 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowGoogleNl(!showGoogleNl)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-content-subtle hover:text-content-muted"
                >
                  {showGoogleNl ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            {settings?.google_nl_api_key && !showGoogleNl && (
              <p className="text-xs text-success mt-1">
                Set: {maskKey(settings.google_nl_api_key)}
              </p>
            )}
          </div>

          {/* Names the listing localization must never translate */}
          <div>
            <label htmlFor="protected-terms" className="block text-sm font-medium text-content-muted mb-1">
              Never translate in store listings
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Game titles and keywords kept in their original spelling in every locale (the wizard, the Play page and Share translations).
              One per line. The app's brand is always added.
            </p>
            <textarea
              id="protected-terms"
              rows={4}
              value={protectedTerms}
              onChange={(e) => setProtectedTerms(e.target.value)}
              placeholder={'Fortune Tiger\nChicken Road'}
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>

          {/* reve.com API Key */}
          <div>
            <label htmlFor="reve-key" className="block text-sm font-medium text-content-muted mb-1">
              reve.com API Key
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Used to render localized caption text onto Play screenshots (Localize screenshots)
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  id="reve-key"
                  type={showReve ? 'text' : 'password'}
                  value={reveKey}
                  onChange={(e) => setReveKey(e.target.value)}
                  placeholder="reve API key"
                  className="block w-full px-3 py-2 pr-10 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowReve(!showReve)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-content-subtle hover:text-content-muted"
                >
                  {showReve ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {settings?.reve_api_key && !showReve && (
              <p className="text-xs text-success mt-1">
                Set: {maskKey(settings.reve_api_key)}
              </p>
            )}
          </div>

          {/* GitHub PAT (Build factory) */}
          <div>
            <label htmlFor="factory-pat" className="block text-sm font-medium text-content-muted mb-1">
              GitHub PAT (Build factory)
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Fine-grained PAT: Contents Read and write + Actions Read on the base Unity repositories
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  id="factory-pat"
                  type={showFactoryPat ? 'text' : 'password'}
                  value={factoryPat}
                  onChange={(e) => setFactoryPat(e.target.value)}
                  placeholder="github_pat_..."
                  className="block w-full px-3 py-2 pr-10 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowFactoryPat(!showFactoryPat)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-content-subtle hover:text-content-muted"
                >
                  {showFactoryPat ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {settings?.factory_github_pat && !showFactoryPat && (
              <p className="text-xs text-success mt-1">
                Set: {maskKey(settings.factory_github_pat)}
              </p>
            )}
          </div>

          {/* Backend host for builds without their own domain */}
          <div>
            <label htmlFor="factory-api-host" className="block text-sm font-medium text-content-muted mb-1">
              Build API host
            </label>
            <p className="text-xs text-content-subtle mb-2">
              The backend domain baked into builds of apps that have no domain of their own. Must route to the API worker.
            </p>
            <input
              id="factory-api-host"
              value={factoryApiHost}
              onChange={(e) => setFactoryApiHost(e.target.value)}
              placeholder="example.xyz"
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
            />
          </div>

          {/* Screenshot translation provider */}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Screenshot translation
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Which service renders the translated caption onto Play screenshots (Localize screenshots).
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setRenderProvider('reve')}
                className={`flex-1 px-3 py-2 rounded-md border text-sm text-left ${
                  renderProvider === 'reve'
                    ? 'border-primary bg-primary/10 text-content'
                    : 'border-border-strong text-content-muted hover:bg-surface-hover'
                }`}
              >
                <span className="block font-medium">reve.com</span>
                <span className="block text-xs text-content-subtle">Surgical caption swap — keeps the image pixel-exact</span>
              </button>
              <button
                type="button"
                onClick={() => setRenderProvider('gpt')}
                className={`flex-1 px-3 py-2 rounded-md border text-sm text-left ${
                  renderProvider === 'gpt'
                    ? 'border-primary bg-primary/10 text-content'
                    : 'border-border-strong text-content-muted hover:bg-surface-hover'
                }`}
              >
                <span className="block font-medium">GPT</span>
                <span className="block text-xs text-content-subtle">gpt-image-2, quality low — re-renders the whole screenshot</span>
              </button>
            </div>
          </div>

          {/* Parallel AI tasks */}
          <div>
            <label htmlFor="ai-task-concurrency" className="block text-sm font-medium text-content-muted mb-1">
              Parallel AI tasks
            </label>
            <p className="text-xs text-content-subtle mb-2">
              How many tasks bulk Play localization and the app wizard each run at once (translations,
              screenshot renders). Lower it if OpenAI or reve start answering &quot;rate limited&quot;;
              6 is the most a Worker can keep open.
            </p>
            <Select
              id="ai-task-concurrency"
              value={taskConcurrency}
              options={['1', '2', '3', '4', '5', '6'].map((n) => ({ value: n, label: n === '1' ? '1 (one by one)' : n }))}
              onChange={setTaskConcurrency}
            />
          </div>

          {/* Template app for new apps */}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Template app for new apps
            </label>
            <p className="text-sm text-content-subtle mb-2">
              When a new app is created in Apps Setup, this app's tracker campaign is copied
              (its link goes into the new app's force offer, left off) and its ad campaigns with
              creatives are copied over. Nothing serves until the app is activated: it is created
              paused with the force offer off. Leave empty to disable.
            </p>
            <Select
              value={templateAppId}
              options={[{ value: '', label: '— no template —' }, ...(apps.data ?? []).map((a) => ({ value: a.id, label: a.name }))]}
              onChange={setTemplateAppId}
              placeholder="— no template —"
              className="w-full"
            />
          </div>

          {/* Email API base URL */}
          <div>
            <label htmlFor="email-api-base" className="block text-sm font-medium text-content-muted mb-1">
              Email API Base URL
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Base URL of the Email → Telegram forwarding worker
            </p>
            <input
              id="email-api-base"
              type="url"
              value={emailApiBase}
              onChange={(e) => setEmailApiBase(e.target.value)}
              placeholder="https://email-to-telegram.example.workers.dev"
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
            />
          </div>

          {/* Email API token */}
          <div>
            <label htmlFor="email-api-token" className="block text-sm font-medium text-content-muted mb-1">
              Email API Token
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Bearer token used to authenticate against the Email API
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  id="email-api-token"
                  type={showEmailToken ? 'text' : 'password'}
                  value={emailApiToken}
                  onChange={(e) => setEmailApiToken(e.target.value)}
                  placeholder="Bearer token"
                  className="block w-full px-3 py-2 pr-10 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowEmailToken(!showEmailToken)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-content-subtle hover:text-content-muted"
                >
                  {showEmailToken ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {settings?.email_api_token && !showEmailToken && (
              <p className="text-xs text-success mt-1">
                Set: {maskKey(settings.email_api_token)}
              </p>
            )}
          </div>

          {/* Google Ads Publish API */}
          <div>
            <label htmlFor="google-ads-api-token" className="block text-sm font-medium text-content-muted mb-1">
              Google Ads Publish API Token
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Bearer token for the "Publish to Google Ads" button on Apps (the API key from its /inbound page). The service pulls the app through our Public API and creates an App campaign.
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  id="google-ads-api-token"
                  type={showGoogleAdsToken ? 'text' : 'password'}
                  value={googleAdsToken}
                  onChange={(e) => setGoogleAdsToken(e.target.value)}
                  placeholder="Bearer token"
                  className="block w-full px-3 py-2 pr-10 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowGoogleAdsToken(!showGoogleAdsToken)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-content-subtle hover:text-content-muted"
                >
                  {showGoogleAdsToken ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {settings?.google_ads_api_token && !showGoogleAdsToken && (
              <p className="text-xs text-success mt-1">
                Set: {maskKey(settings.google_ads_api_token)}
              </p>
            )}
            <input
              id="google-ads-api-base"
              type="url"
              value={googleAdsBase}
              onChange={(e) => setGoogleAdsBase(e.target.value)}
              placeholder="https://web-production-2ba7bf.up.railway.app (default)"
              aria-label="Google Ads Publish API base URL"
              className="mt-2 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
            />
            <p className="text-xs text-content-subtle mt-1">Base URL — leave empty for the default instance.</p>
          </div>

          {/* Google Ads conversions via Firebase */}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Google Ads conversions (Firebase)
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Reg and dep go back as Firebase events of the app (GA4 Measurement Protocol) for clicks the Android app
              stamped with its app_instance_id. In Google Ads import these events as conversions from the linked Firebase
              property. The secret per app is on Apps → Firebase.
            </p>
            <label className="flex items-center gap-2 text-sm text-content cursor-pointer mb-2">
              <input type="checkbox" checked={ga4Enabled} onChange={(e) => setGa4Enabled(e.target.checked)} />
              Send conversions
            </label>
            <div className="grid grid-cols-2 gap-3">
              <input
                value={ga4EventReg}
                onChange={(e) => setGa4EventReg(e.target.value)}
                placeholder="zw_reg (default)"
                aria-label="Registration event name"
                className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
              />
              <input
                value={ga4EventDep}
                onChange={(e) => setGa4EventDep(e.target.value)}
                placeholder="zw_dep (default)"
                aria-label="Deposit event name"
                className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
              />
            </div>
            <p className="text-xs text-content-subtle mt-1">
              Event names: letters, digits and _, not starting with firebase_, google_ or ga_. Empty or invalid = default.
              Renaming after the conversion is set up in Google Ads breaks the import.
            </p>
          </div>

          {/* PWA mirror base */}
          <div>
            <label htmlFor="pwa-mirror-base" className="block text-sm font-medium text-content-muted mb-1">
              PWA Mirror Base URL
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Non-Cloudflare reverse proxy (e.g. Deno Deploy) used as fallback when CF is blocked. Used by the "Use mirror" autofill on PWA Proxy URL.
            </p>
            <input
              id="pwa-mirror-base"
              type="url"
              value={pwaMirrorBase}
              onChange={(e) => setPwaMirrorBase(e.target.value)}
              placeholder="https://mirror.example.com"
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
            />
          </div>

          {/* Антифрод: движение */}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Антифрод: движение
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Пороги детекта эмулятора по акселерометру. Подбираются по гистограмме на странице Devices.
              Флаг только наблюдается и не блокирует выдачу. Гравитация Земли ≈ 9806 мм/с².
              Блокирует только полностью замёрзшее окно — гейт «Замёрзший сенсор» ниже.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <ThresholdField
                id="motion-stddev-min"
                label="Мин. разброс, мм/с²"
                value={motionStddevMin}
                onChange={setMotionStddevMin}
              />
              <ThresholdField
                id="motion-dup-max"
                label="Макс. дублей, %"
                value={motionDupMax}
                onChange={setMotionDupMax}
              />
              <ThresholdField
                id="motion-g-min"
                label="Мин. гравитация, мм/с²"
                value={motionGMin}
                onChange={setMotionGMin}
              />
              <ThresholdField
                id="motion-g-max"
                label="Макс. гравитация, мм/с²"
                value={motionGMax}
                onChange={setMotionGMax}
              />
            </div>
          </div>

          {/* Антифрод: гейты по сигналам устройства */}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Антифрод: гейты по сигналам устройства
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Блокируют force offer, рекламу не трогают. Выключенный гейт перестаёт срабатывать
              в течение 5 минут после сохранения, без деплоя; уже заблокированные им устройства
              разблокируются на следующей сессии.
            </p>
            <div className="space-y-2">
              {SIGNAL_GATES.map((gate) => (
                <label key={gate.key} className="flex items-start gap-2 text-sm text-content cursor-pointer">
                  <input
                    type="checkbox"
                    checked={signalGates[gate.key]}
                    onChange={(e) => setSignalGates((g) => ({ ...g, [gate.key]: e.target.checked }))}
                    className="mt-0.5"
                  />
                  <span>
                    {gate.label}
                    <span className="block text-xs text-content-subtle">{gate.help}</span>
                  </span>
                </label>
              ))}
            </div>
            <label className="flex items-start gap-2 text-sm text-content cursor-pointer mt-3">
              <input
                type="checkbox"
                checked={humanPass}
                onChange={(e) => setHumanPass(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Пропускать тех, кого проверка признала человеком
                <span className="block text-xs text-content-subtle">
                  После блока SDK до 30 секунд смотрит, как играют, и присылает цифры; оценку считает сервер
                  (Devices → Human check). Включено: устройство с оценкой «человек» со следующего запуска не
                  блокируется и видит оффер. Эмулятор и флот сканеров не пропускаются никогда.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-content cursor-pointer mt-3">
              <input
                type="checkbox"
                checked={behaviorGate}
                onChange={(e) => setBehaviorGate(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Блокировать тех, кого проверка признала ботом
                <span className="block text-xs text-content-subtle">
                  Причина «Bot by behavior»: устройство с оценкой «бот» блокируется на следующей проверке, даже если
                  остальные гейты чисты (симку вставили, вернулись домой). Выключили — блок уходит на следующей проверке.
                </span>
              </span>
            </label>
            <div className="mt-3">
              <p className="text-xs text-content-subtle mb-2">
                Пороги оценки (0–100). «Бот»: вероятность бота не ниже порога и данных собрано не меньше.
                «Человек»: вероятность ниже порога и данных достаточно. Всё между — «не знаем», блок остаётся.
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <ThresholdField id="session-bot-min" label="Бот: вероятность от, %" value={sessionCfg.bot_min}
                  onChange={(v) => setSessionCfg((c) => ({ ...c, bot_min: v }))} />
                <ThresholdField id="session-bot-conf" label="Бот: данных от, %" value={sessionCfg.bot_min_confidence}
                  onChange={(v) => setSessionCfg((c) => ({ ...c, bot_min_confidence: v }))} />
                <ThresholdField id="session-human-max" label="Человек: вероятность ниже, %" value={sessionCfg.human_max}
                  onChange={(v) => setSessionCfg((c) => ({ ...c, human_max: v }))} />
                <ThresholdField id="session-human-conf" label="Человек: данных от, %" value={sessionCfg.human_min_confidence}
                  onChange={(v) => setSessionCfg((c) => ({ ...c, human_min_confidence: v }))} />
              </div>
              <p className="text-xs text-content-subtle mt-3 mb-2">
                Сбор разметки для модели. Уверенных ботов (эмулятор, флот сканеров) проверка не разблокирует, но их
                цифры — примеры «бота». Незаблокированные устройства — будущие примеры «человека», когда с них придёт
                регистрация; SDK должен уметь слать проверку и без блока.
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <ThresholdField id="session-sample-sure" label="Просить у уверенных ботов, %" value={sessionCfg.sample_sure_bot}
                  onChange={(v) => setSessionCfg((c) => ({ ...c, sample_sure_bot: v }))} />
                <ThresholdField id="session-sample-clean" label="Просить у чистых устройств, %" value={sessionCfg.sample_clean}
                  onChange={(v) => setSessionCfg((c) => ({ ...c, sample_clean: v }))} />
              </div>
            </div>
          </div>

          {/* Пауза force offer после выхода в стор */}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Пауза оффера после выхода в стор
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Ревьюеры и сканеры приходят волной в первые часы после модерации. Всё это время force offer
              молчит у всех, кроме наших QA-устройств и переходов из своей сети; реклама не трогается.
              Невышедшее приложение оффер не выдаёт. Сколько осталось — в Apps, колонка Traffic, там же
              «Turn on» снимает паузу у одного приложения. Изменения применяются в течение 5 минут.
            </p>
            <label className="flex items-start gap-2 text-sm text-content cursor-pointer mb-3">
              <input type="checkbox" checked={offerHoldOn} onChange={(e) => setOfferHoldOn(e.target.checked)} className="mt-0.5" />
              <span>
                Держать паузу
                <span className="block text-xs text-content-subtle">Выключено — оффер включается сразу, как раньше, всплески не ловятся.</span>
              </span>
            </label>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <ThresholdField id="offer-hold-release" label="После релиза, ч" value={offerHold.release_hours}
                onChange={(v) => setOfferHold((h) => ({ ...h, release_hours: v }))} />
              <ThresholdField id="offer-hold-ramp" label="Потом плавно включать, ч" value={offerHold.ramp_hours}
                onChange={(v) => setOfferHold((h) => ({ ...h, ramp_hours: v }))} />
              <ThresholdField id="offer-hold-meta" label="После апдейта меты, ч" value={offerHold.meta_hours}
                onChange={(v) => setOfferHold((h) => ({ ...h, meta_hours: v }))} />
              <ThresholdField id="offer-hold-build" label="После апдейта билда, ч" value={offerHold.build_hours}
                onChange={(v) => setOfferHold((h) => ({ ...h, build_hours: v }))} />
            </div>
            <p className="text-xs text-content-subtle mt-2">
              Плавное включение: доля устройств, которым показывается оффер, растёт от 0 до 100% за указанные часы;
              одно и то же устройство не мигает. Выход меты и билда берётся из Play API (переход в Published),
              а у приложений без API — из стора: билд — по смене версии (до 30 минут), мета — по слежке за листингом (раз в сутки).
            </p>
            <label className="flex items-start gap-2 text-sm text-content cursor-pointer mt-3 mb-2">
              <input type="checkbox" checked={offerSpikeOn} onChange={(e) => setOfferSpikeOn(e.target.checked)} className="mt-0.5" />
              <span>
                Выключать оффер на всплеске новых устройств
                <span className="block text-xs text-content-subtle">
                  Раз в 5 минут: новых устройств за последний час не меньше порога и во столько-то раз больше
                  обычного часа за прошлую неделю — оффер молчит указанные часы после последнего всплеска,
                  в топик офферов уходит карточка.
                </span>
              </span>
            </label>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <ThresholdField id="offer-hold-spike-min" label="Новых устройств за час, от" value={offerHold.spike_min}
                onChange={(v) => setOfferHold((h) => ({ ...h, spike_min: v }))} />
              <ThresholdField id="offer-hold-spike-mult" label="Во сколько раз больше обычного" value={offerHold.spike_mult}
                onChange={(v) => setOfferHold((h) => ({ ...h, spike_mult: v }))} />
              <ThresholdField id="offer-hold-spike-hours" label="Пауза после всплеска, ч" value={offerHold.spike_hours}
                onChange={(v) => setOfferHold((h) => ({ ...h, spike_hours: v }))} />
            </div>
          </div>

          {/* Слежка за листингом в Google Play */}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Слежка за листингом в Google Play
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Раз в сутки читает публичную страницу каждого живого Android-приложения по всем его
              локалям: название, описания, иконку, фичер, скриншоты, видео. Изменение, подтверждённое
              повторным чтением, пишется в историю (Apps → Store package) и уходит карточкой в релизный топик.
            </p>
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm text-content cursor-pointer">
                <input type="checkbox" checked={listingWatch} onChange={(e) => setListingWatch(e.target.checked)} className="mt-0.5" />
                <span>
                  Читать стор
                  <span className="block text-xs text-content-subtle">Выключено — страницы не читаются вовсе, история не пополняется.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-content cursor-pointer">
                <input type="checkbox" checked={listingWatchAlerts} onChange={(e) => setListingWatchAlerts(e.target.checked)} className="mt-0.5" />
                <span>
                  Карточка в Telegram при изменении
                  <span className="block text-xs text-content-subtle">Выключено — изменения только в истории, без сообщений.</span>
                </span>
              </label>
            </div>
          </div>

          {/* Свой домен у каждого нового приложения */}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Домены приложений
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Первая сборка нового приложения покупает ему домен в Cloudflare Registrar: случайное имя,
              самое дешёвое за первый год из зон ниже, не дороже потолка (цена продления не учитывается). Домен
              привязывается к API, трекер-ссылки приложения переезжают на него, сборка передаёт его SDK
              как адрес API. Покупка списывается с карты Cloudflare-аккаунта и не возвращается.
            </p>
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm text-content cursor-pointer">
                <input type="checkbox" checked={appDomains} onChange={(e) => setAppDomains(e.target.checked)} className="mt-0.5" />
                <span>
                  Покупать домены
                  <span className="block text-xs text-content-subtle">Выключено — новые сборки идут на общем хосте, как раньше.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-content cursor-pointer">
                <input type="checkbox" checked={appDomainAutoRenew} onChange={(e) => setAppDomainAutoRenew(e.target.checked)} className="mt-0.5" />
                <span>
                  Автопродление
                  <span className="block text-xs text-content-subtle">Ставится при покупке. Без него домен истечёт через год вместе со ссылками и API приложения.</span>
                </span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_10rem] gap-3">
                <div>
                  <label htmlFor="app-domain-tlds" className="block text-xs text-content-subtle mb-1">Зоны (до 20, через запятую)</label>
                  <input
                    id="app-domain-tlds"
                    value={appDomainTlds}
                    onChange={(e) => setAppDomainTlds(e.target.value)}
                    className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
                <ThresholdField id="app-domain-max-price" label="Потолок, $ за 1-й год" value={appDomainMaxPrice} onChange={setAppDomainMaxPrice} />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-between">
          <div>
            {saved && (
              <span className="text-sm text-success flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved
              </span>
            )}
            {saveMutation.isError && (
              <span className="text-sm text-danger">
                Failed to save: {saveMutation.error instanceof Error ? saveMutation.error.message : 'Unknown error'}
              </span>
            )}
          </div>
          <EditOnly>
            <Button
              onClick={handleSave}
              loading={saveMutation.isPending}
            >
              Save API Keys
            </Button>
          </EditOnly>
        </div>
      </div>

      <TelegramPrSettings />

      <UnityVersionsSettings />

      {/* DB Backups */}
      <div className="bg-surface border border-border rounded-lg mt-6">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium text-content">DB Backups</h2>
          <p className="text-sm text-content-subtle mt-1">
            Backups run at 03:00 and 15:00 UTC. A download link is posted to this Telegram chat.
          </p>
        </div>
        <div className="px-6 py-5">
          <label htmlFor="backup-chat-id" className="block text-sm font-medium text-content-muted mb-1">
            Backup channel chat_id
          </label>
          <p className="text-xs text-content-subtle mb-2">
            Add the bot to a private channel, then paste the channel's chat_id (e.g. -1001234567890). Empty = no Telegram messages, backups still run.
          </p>
          <input
            id="backup-chat-id"
            type="text"
            value={backupChatId}
            onChange={(e) => setBackupChatId(e.target.value)}
            placeholder="-100..."
            className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
          />
        </div>

        {/* Save Button */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-between">
          <div>
            {backupStarted && (
              <span className="text-sm text-success">
                Backup queued — it starts within a minute; the result will arrive in the Telegram channel
              </span>
            )}
            {!backupStarted && backupMutation.isError && (
              <span className="text-sm text-danger">
                Failed to start backup: {backupMutation.error instanceof Error ? backupMutation.error.message : 'Unknown error'}
              </span>
            )}
            {!backupStarted && !backupMutation.isError && saved && (
              <span className="text-sm text-success flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved
              </span>
            )}
            {saveMutation.isError && (
              <span className="text-sm text-danger">
                Failed to save: {saveMutation.error instanceof Error ? saveMutation.error.message : 'Unknown error'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <EditOnly>
              <Button
                variant="secondary"
                onClick={() => backupMutation.mutate()}
                loading={backupMutation.isPending}
              >
                Backup now
              </Button>
            </EditOnly>
            <EditOnly>
              <Button
                onClick={handleSave}
                loading={saveMutation.isPending}
              >
                Save
              </Button>
            </EditOnly>
          </div>
        </div>
      </div>

      {/* Brand */}
      <div className="bg-surface border border-border rounded-lg mt-6">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium text-content">Brand</h2>
          <p className="text-sm text-content-subtle mt-1">
            The product name shown in the dashboard, on public forms, in Telegram alerts and API docs.
            Leave empty for the default ({DEFAULT_BRAND_NAME}).
          </p>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div>
            <label htmlFor="brand-name" className="block text-sm font-medium text-content-muted mb-1">
              Product name
            </label>
            <input
              id="brand-name"
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder={DEFAULT_BRAND_NAME}
              className="block w-full max-w-md px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
            />
            <p className="text-xs text-content-subtle mt-2">Saved together with the settings above.</p>
          </div>
        </div>
      </div>

      {/* Display preferences */}
      <div className="bg-surface border border-border rounded-lg mt-6">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium text-content">Display</h2>
          <p className="text-sm text-content-subtle mt-1">
            Local preferences for this browser only. All timestamps are stored in UTC on the server.
          </p>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div>
            <label htmlFor="tz" className="block text-sm font-medium text-content-muted mb-1">
              Timezone
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Used to render dates and times across the dashboard. Default: browser timezone ({browserTz}).
            </p>
            <div className="flex items-center gap-2">
              <select
                id="tz"
                value={tz}
                onChange={(e) => setTz(e.target.value)}
                className="block w-full max-w-md px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              >
                <option value="">Browser default ({browserTz})</option>
                {zones.map((z) => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select>
              <Button
                onClick={() => {
                  setTimezone(tz);
                  setTzSaved(true);
                  setTimeout(() => setTzSaved(false), 2000);
                  setTimeout(() => window.location.reload(), 400);
                }}
              >
                Save
              </Button>
            </div>
            {tzSaved && (
              <p className="text-xs text-success mt-2">Saved. Reloading…</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
